import { RiCheckFill } from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, ReactNode,
} from "react";
import { Link as LinkButton, LinkButtonProps, PathValues } from "../../../contexts/Routing";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

export type LinkProps<T extends PathValues> = LinkButtonProps<T> & {
    isActive?: boolean;
    className?: string;
    isChecked?: boolean;
    postFixItem?: ReactNode;
    prefixIcon?: ReactNode;
    target?: string;
    iconSize?: "xsmall" | "small" | "medium" | "large";
    metricPath?: string[];
    to: T,
}

const Link = React.forwardRef(<P extends PathValues>(
    args: LinkProps<P>,
    ref: React.ForwardedRef<HTMLAnchorElement>,
): ReactElement => {
    const {
        isActive,
        className,
        isChecked,
        children,
        postFixItem,
        prefixIcon,
        iconSize,
        metricPath,
        onClick,
        ...rest
    } = args;

    const clickHandler = metricPath ? LoggingHandler.instance.wrapMetricWithSnapshot(metricPath, onClick) : onClick;

    const LinkButtonItem = LinkButton as unknown as ((args: typeof rest) => ReactNode);

    return (
        <LinkButtonItem
            {...rest}
            ref={ref}
            className={classNames("dropdown-item", className, { active: isActive })}
            onClick={clickHandler}
        >
            {prefixIcon && (
                <span className={classNames(
                    "dropdown-item-prefix-icon",
                    iconSize ? `dropdown-item-prefix-icon-${iconSize}` : "",
                )}
                >
                    {prefixIcon}
                </span>
            )}
            {children && (
                <div className="dropdown-item-content">
                    {children}
                </div>
            )}

            {isChecked && <span><RiCheckFill /></span>}
            {postFixItem}
        </LinkButtonItem>

    );
});

Link.defaultProps = {
    className: "",
    isActive: false,
    postFixItem: undefined,
    prefixIcon: undefined,
    iconSize: "medium",
    target: undefined,
    metricPath: undefined,
};

const LinkItem = Link as <
    T extends PathValues,
> (
    props: LinkProps<T>
) => ReturnType<typeof Link>;

export default LinkItem;
